<template>
  <!--
    切換帳號（2026-10-30）：這台裝置登入過的帳號（最多 5 個），點一下換過去。
    開啟：store.commit("SetAccountSwitcher", true)（個人中心、頭像選單）。
    清單在 utils/knownAccounts.js，切換流程在 utils/accountSwitch.js。
  -->
  <v-dialog :value="show" max-width="420" scrollable @input="onInput">
    <v-card class="account-switch">
      <div class="account-switch__head">
        <span class="account-switch__title">切換帳號</span>
        <v-btn icon aria-label="關閉" :disabled="busy" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-progress-linear :active="busy" indeterminate color="primary" height="3"></v-progress-linear>
      <v-card-text class="pa-0">
        <v-list class="py-0">
          <v-list-item
            v-for="a in accounts"
            :key="a.email"
            :disabled="busy"
            class="account-switch__item"
            @click="choose(a)"
          >
            <v-list-item-avatar color="blue darken-1" size="40">
              <v-img v-if="a.photoUrl" :src="a.photoUrl" :alt="a.name || a.email"></v-img>
              <span v-else class="white--text account-switch__initials">{{ initials(a) }}</span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">{{ a.name || a.email }}</v-list-item-title>
              <v-list-item-subtitle>{{ a.email }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <span v-if="isCurrent(a)" class="account-switch__badge">目前</span>
              <v-btn
                v-else
                icon
                :disabled="busy"
                :aria-label="`從這台裝置移除 ${a.email}`"
                title="從這台裝置移除"
                @click.stop="remove(a)"
              >
                <v-icon color="grey">mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item :disabled="busy" class="account-switch__item" @click="choose(null)">
            <v-list-item-avatar size="40" class="account-switch__add">
              <v-icon color="primary">mdi-account-plus-outline</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="primary--text font-weight-medium">登入其他帳號</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <p class="account-switch__hint">
          這台裝置最多記住 {{ max }} 個帳號，超過時會移除最久沒用的。共用的裝置用完，請把帳號從這裡移除。
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { listKnownAccounts, forgetAccount, sameEmail, MAX_KNOWN_ACCOUNTS } from "@/utils/knownAccounts";
import { switchToAccount, SwitchError } from "@/utils/accountSwitch";

export default {
  name: "AccountSwitchDialog",
  data() {
    return {
      accounts: [],
      busy: false,
      max: MAX_KNOWN_ACCOUNTS,
    };
  },
  computed: {
    show() {
      return this.$store.state.accountSwitcher;
    },
    currentEmail() {
      return this.$store.state.userRealEmail;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) this.refresh();
      },
    },
  },
  methods: {
    /** 目前的帳號排第一；還沒記進清單時（身分還在同步）先用 store 的資料補一筆 */
    refresh() {
      const list = listKnownAccounts();
      const current = list.find((a) => this.isCurrent(a));
      const others = list.filter((a) => !this.isCurrent(a));
      const state = this.$store.state;
      const me = current || (this.currentEmail ? { email: this.currentEmail, name: state.userRealName, photoUrl: state.userPhoto } : null);
      this.accounts = me ? [me, ...others] : others;
    },
    isCurrent(a) {
      return !!this.currentEmail && sameEmail(a.email, this.currentEmail);
    },
    initials(a) {
      const name = (a.name || "").trim();
      if (name) return Array.from(name).slice(0, 2).join("");
      return (a.email || "").slice(0, 2).toUpperCase();
    },
    onInput(value) {
      if (!value) this.close();
    },
    close() {
      if (this.busy) return;
      this.$store.commit("SetAccountSwitcher", false);
    },
    async choose(account) {
      if (account && this.isCurrent(account)) {
        this.close();
        return;
      }
      this.busy = true;
      try {
        const result = await switchToAccount(account);
        // 直接登入成功的話頁面正在重新載入，維持轉圈；開了登入框就把這個收起來
        if (result === "reload") return;
        this.busy = false;
        this.$store.commit("SetAccountSwitcher", false);
      } catch (e) {
        this.busy = false;
        this.$notify({ content: e instanceof SwitchError ? e.message : "切換失敗，請稍後再試。", status: "error" });
      }
    },
    async remove(account) {
      await forgetAccount(account.email);
      this.refresh();
      this.$notify({ content: "已從這台裝置移除", status: "success" });
    },
  },
};
</script>

<style scoped>
.account-switch__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 8px 8px 20px;
}

.account-switch__title {
  font-size: 18px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}

.account-switch__item {
  min-height: 64px;
}

.account-switch__initials {
  font-size: 14px;
}

.account-switch__badge {
  padding: 2px 10px;
  border: 1px solid #7c4dff;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  color: #7c4dff;
  white-space: nowrap;
}

.account-switch__add {
  background: #f3effe;
}

.account-switch__hint {
  margin: 0;
  padding: 12px 20px 16px;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.6);
}
</style>
